import React, { useId, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Paper,
  Popper,
  PopperProps,
  Portal,
  Typography,
} from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTranslation } from 'react-i18next';
import {
  EMPLOYEE_DOC_REQUIREMENT_LABEL_KEYS,
  EMPLOYEE_DOC_REQUIREMENTS,
  EmployeeDocRequirement,
} from './types';
import styles from './LibraryList.module.scss';

/** Same breakpoint as `breakpoints.$tablet-mobile` in scss/settings/_breakpoints.scss. */
const TABLET_MOBILE_QUERY = '(max-width: 991px)';

/** Gap between the filter block and its anchor (desktop) / the viewport edges (tablet, mobile). */
const FILTER_BLOCK_GAP = 8;

/**
 * Tablet / mobile: the filter block is pinned to the top left corner of the viewport, so it is
 * anchored to a zero-size virtual element at (0, 0) and positioned with `strategy: 'fixed'`.
 */
const VIEWPORT_TOP_LEFT_ANCHOR: PopperProps['anchorEl'] = {
  getBoundingClientRect: () => new DOMRect(0, 0, 0, 0),
};

type RequirementFilterProps = {
  /** The applied selection (from the URL). */
  value: EmployeeDocRequirement[];
  /** Applies a new selection; an empty one clears the filter. */
  onApply: (value: EmployeeDocRequirement[]) => void;
};

type FilterBlockType = {
  popperId: string;
  /** The "Filter by Requirement" button (desktop placement). */
  anchorEl: HTMLElement | null;
  /** Tablet / mobile: pinned to the top left corner instead of the button. */
  pinnedTopLeft: boolean;
  open: boolean;
  handleClose: () => void;
  handleApply: () => void;
  handleReset: () => void;
  resetDisabled: boolean;
  handleCheckboxChange: (value: EmployeeDocRequirement, checked: boolean) => void;
  draft: EmployeeDocRequirement[];
};

const FilterBlock = ({
  popperId,
  anchorEl,
  pinnedTopLeft,
  draft,
  handleCheckboxChange,
  handleApply,
  handleReset,
  resetDisabled,
  handleClose,
  open,
}: FilterBlockType) => {
  const { t } = useTranslation();
  return (
    <>
      {open && (
        // Transparent overlay behind the filter block: a click outside the block lands here and
        // closes it (discarding the draft) instead of reaching the page underneath. Portaled to
        // <body>, so no parent container can clip it or trap it in its stacking context.
        <Portal>
          <div
            id='libraryListRequirementFilterOverlay'
            className={styles.requirement_filter_overlay}
            onClick={handleClose}
            aria-hidden='true'
          />
        </Portal>
      )}
      <Popper
        id={popperId}
        open={open && (pinnedTopLeft || Boolean(anchorEl))}
        anchorEl={pinnedTopLeft ? VIEWPORT_TOP_LEFT_ANCHOR : anchorEl}
        placement='bottom-start'
        className={`${styles.requirement_filter_popper}${
          pinnedTopLeft ? ` ${styles.requirement_filter_popper_pinned}` : ''
        }`}
        popperOptions={{ strategy: pinnedTopLeft ? 'fixed' : 'absolute' }}
        modifiers={[
          {
            name: 'offset',
            options: {
              // [skidding (x), distance (y)]: below the button / inset from the viewport corner.
              offset: pinnedTopLeft ? [FILTER_BLOCK_GAP, FILTER_BLOCK_GAP] : [0, FILTER_BLOCK_GAP],
            },
          },
          // The pinned block must stay in the corner, not be pushed / flipped around.
          { name: 'flip', enabled: !pinnedTopLeft },
          { name: 'preventOverflow', enabled: !pinnedTopLeft },
        ]}
      >
        <Paper
          role='dialog'
          aria-labelledby={`${popperId}-title`}
          elevation={8}
          className={styles.requirement_filter_paper}
          onKeyDown={(event) => {
            if (event.key === 'Escape') {
              handleClose();
              anchorEl?.focus();
            }
          }}
        >
          <Typography
            id={`${popperId}-title`}
            variant='subtitle2'
            className={styles.requirement_filter_title}
          >
            {t('libraryTable.filterByRequirement')}
          </Typography>
          <FormGroup className={styles.requirement_filter_options}>
            {EMPLOYEE_DOC_REQUIREMENTS.map((requirement) => (
              <FormControlLabel
                key={requirement}
                className={styles.requirement_filter_option}
                control={
                  <Checkbox
                    id={`libraryListRequirementFilterCheckbox_${requirement}`}
                    size='small'
                    checked={draft.includes(requirement)}
                    onChange={(event) => handleCheckboxChange(requirement, event.target.checked)}
                  />
                }
                label={t(EMPLOYEE_DOC_REQUIREMENT_LABEL_KEYS[requirement])}
              />
            ))}
          </FormGroup>
          <Box className={styles.requirement_filter_actions}>
            <Button
              id='libraryListRequirementFilterResetBtn'
              variant='text'
              onClick={handleReset}
              disabled={resetDisabled}
              className={styles.requirement_filter_reset}
            >
              {t('libraryTable.reset')}
            </Button>
            <Box className={styles.requirement_filter_main_actions}>
              <Button
                id='libraryListRequirementFilterCloseBtn'
                variant='contained'
                onClick={handleClose}
                className='btn-default btn-action-basic btn-cancel'
              >
                {t('libraryTable.close')}
              </Button>
              <Button
                id='libraryListRequirementFilterApplyBtn'
                variant='contained'
                onClick={handleApply}
                className='btn-default btn-action-basic'
              >
                {t('libraryTable.filter')}
              </Button>
            </Box>
          </Box>
        </Paper>
      </Popper>
    </>
  );
};

/**
 * "Filter by Requirement" button shown at the bottom of the Library page aside. Opens a popper (not a full-screen
 * modal) with a checkbox per `employee_doc_requirements` value: below the button on desktop, in the top left
 * corner of the screen on tablet / mobile. The checkboxes are a draft: only "Filter" applies
 * them, "Close" / a click on the transparent overlay behind the block / Escape discards the
 * changes, "Reset" clears the filter.
 */
export const RequirementFilter = ({ value, onApply }: RequirementFilterProps) => {
  const { t } = useTranslation();
  const popperId = useId();
  const pinnedTopLeft = useMediaQuery(TABLET_MOBILE_QUERY);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<EmployeeDocRequirement[]>(value);

  const handleToggle = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!open) {
      // Start from the applied selection every time the popper opens.
      setDraft(value);
      setAnchorEl(event.currentTarget);
    }
    setOpen((prev) => !prev);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleCheckboxChange = (requirement: EmployeeDocRequirement, checked: boolean) => {
    setDraft((prev) =>
      EMPLOYEE_DOC_REQUIREMENTS.filter((item) =>
        item === requirement ? checked : prev.includes(item)
      )
    );
  };

  const handleApply = () => {
    onApply(draft);
    setOpen(false);
  };

  /** Unchecks everything and removes the applied filter (shows all the items again). */
  const handleReset = () => {
    setDraft([]);
    if (value.length > 0) {
      onApply([]);
    }
    setOpen(false);
  };

  const filterActive = value.length > 0;

  return (
    <>
      <Button
        id='libraryListRequirementFilterBtn'
        variant='outlined'
        onClick={handleToggle}
        aria-haspopup='dialog'
        aria-expanded={open}
        aria-controls={open ? popperId : undefined}
        startIcon={
          <Badge color='primary' variant='dot' invisible={!filterActive}>
            <FilterListIcon id='libraryListRequirementFilterIcon' fontSize='small' />
          </Badge>
        }
        className={`${styles.requirement_filter_button}${
          filterActive ? ` ${styles.requirement_filter_button_active}` : ''
        }`}
      >
        {t('libraryTable.filterByRequirement')}
      </Button>
      <FilterBlock
        popperId={popperId}
        anchorEl={anchorEl}
        pinnedTopLeft={pinnedTopLeft}
        handleClose={handleClose}
        handleApply={handleApply}
        handleReset={handleReset}
        resetDisabled={!filterActive && draft.length === 0}
        handleCheckboxChange={handleCheckboxChange}
        draft={draft}
        open={open}
      />
    </>
  );
};

export default RequirementFilter;
